<div class="step-panel esign-container">

  <!-- TOP HEADER / LOAN SUMMARY BAR -->
  <div class="esign-header-card">
    <div class="header-main">
      <div class="header-titles">
        <div class="badge-row">
          <span class="badge b-v">STEP 10 OF 13</span>
          <span class="badge b-b">Legal Execution · Digio Gateway</span>
          <span class="badge b-g" *ngIf="pdfStored">✓ Executed &amp; Stamped</span>
        </div>
        <h2 class="pg-title">e-Agreement &amp; Digital Signature</h2>
        <p class="pg-sub">
          Digital execution of the loan agreement under <strong>Section 5 of Information Technology Act, 2000</strong>.
          Aadhaar OTP e-Sign verified by Certifying Authority (CCA/NSDL) with automated NBFC DSC counter-signing.
        </p>
      </div>

      <!-- Quick Terms Metric Chips -->
      <div class="loan-terms-strip" *ngIf="sanctionedAmount > 0">
        <div class="term-pill">
          <span class="tp-lbl">Sanctioned Amount</span>
          <span class="tp-val">₹ {{ sanctionedAmount | number:'1.0-0' }}</span>
        </div>
        <div class="term-pill">
          <span class="tp-lbl">Interest Rate</span>
          <span class="tp-val">{{ interestRate }}% p.a.</span>
        </div>
        <div class="term-pill">
          <span class="tp-lbl">Tenor</span>
          <span class="tp-val">{{ tenorMonths }} Months</span>
        </div>
        <div class="term-pill highlight" *ngIf="emiAmount > 0">
          <span class="tp-lbl">Monthly EMI</span>
          <span class="tp-val">₹ {{ emiAmount | number:'1.0-0' }}</span>
        </div>
      </div>
    </div>
  </div>

  <!-- LEGAL COMPLIANCE NOTICE -->
  <div class="alert violet legal-banner">
    <div class="alert-icon">⚖️</div>
    <div class="alert-text">
      <strong>Legally Enforceable Digital Contract</strong>
      <span>
        Aadhaar OTP based eSign carries legal equivalence to wet-ink physical signature under Section 3A &amp; 5 of the Information Technology Act 2000 and Section 65B of Indian Evidence Act.
      </span>
    </div>
  </div>

  <!-- MAIN 2-COLUMN GRID -->
  <div class="g2 esign-main-grid">

    <!-- LEFT COLUMN: Agreement & Signer Details + Actions -->
    <div class="left-col">

      <!-- CARD 1: LOAN AGREEMENT & STAMP DETAILS -->
      <div class="card">
        <div class="card-title">
          <div class="ct-bar"></div>
          <span>Agreement &amp; e-Stamp Specifications</span>
        </div>

        <div class="spec-grid">
          <div class="spec-item">
            <span class="spec-lbl">Agreement Reference</span>
            <span class="spec-val font-mono">{{ agreementRef }}</span>
          </div>
          <div class="spec-item">
            <span class="spec-lbl">e-Stamp Certificate</span>
            <span class="spec-val font-mono cert-badge">
              📜 {{ eStampCertNo }}
            </span>
          </div>
          <div class="spec-item">
            <span class="spec-lbl">Stamp Duty Paid</span>
            <span class="spec-val">₹ {{ eStampDuty }} ({{ borrowerState }})</span>
          </div>
          <div class="spec-item">
            <span class="spec-lbl">eSign ASP Gateway</span>
            <span class="spec-val">Digio Technologies (CCA / UIDAI)</span>
          </div>
        </div>

        <div class="doc-file-chip" *ngIf="agreementRef">
          <div class="doc-icon">📄</div>
          <div class="doc-info">
            <div class="doc-name">Loan_Agreement_{{ agreementRef }}_stamped.pdf</div>
            <div class="doc-meta">Tamper-Evident · 256-Bit Encrypted · eStamp Attached as Page 1</div>
          </div>
        </div>
      </div>

      <!-- CARD 2: BORROWER SIGNER PROFILE -->
      <div class="card">
        <div class="card-title">
          <div class="ct-bar"></div>
          <span>Signatory (Borrower) KYC Identity</span>
        </div>

        <div class="signer-profile">
          <div class="signer-avatar">
            {{ borrowerName ? borrowerName.charAt(0).toUpperCase() : 'B' }}
          </div>
          <div class="signer-details">
            <div class="signer-name">{{ borrowerName }}</div>
            <div class="signer-sub">
              <span>📱 {{ borrowerMobile || 'Mobile Verified' }}</span>
              <span class="dot-sep">·</span>
              <span>✉️ {{ borrowerEmail || 'Email Verified' }}</span>
            </div>
          </div>
        </div>

        <div class="kyc-spec-row">
          <div class="k-chip">
            <span class="k-lbl">Aadhaar (Masked)</span>
            <span class="k-val">{{ borrowerAadhaarMask }}</span>
          </div>
          <div class="k-chip">
            <span class="k-lbl">PAN</span>
            <span class="k-val">{{ borrowerPan }}</span>
          </div>
          <div class="k-chip">
            <span class="k-lbl">Jurisdiction State</span>
            <span class="k-val">{{ borrowerState }}</span>
          </div>
        </div>
      </div>

      <!-- CARD 3: EXECUTION ACTIONS -->
      <div class="card action-card">
        <div class="card-title">
          <div class="ct-bar"></div>
          <span>Digital Signature Execution</span>
        </div>

        <div class="action-buttons-stack">
          <!-- Primary: In-App Digio Web SDK Popup -->
          <button class="btn btn-pri btn-lg w-100 sign-btn"
            (click)="startDirectESign()"
            [disabled]="isLoading || isSigning || borrowerSigned">
            <span *ngIf="isSigning" class="spinner-border spinner-border-sm me-2"></span>
            <span *ngIf="!isSigning && !borrowerSigned">✍️ Sign via Aadhaar OTP (Digio In-App)</span>
            <span *ngIf="borrowerSigned">✓ Borrower Signed via Aadhaar OTP</span>
          </button>

          <!-- Secondary Actions Row -->
          <div class="secondary-actions-row">
            <button class="btn btn-sec flex-1"
              (click)="sendESignLink()"
              [disabled]="isSendingLink || borrowerSigned">
              <span *ngIf="isSendingLink" class="spinner-border spinner-border-sm me-1"></span>
              {{ isSendingLink ? 'Dispatching Link...' : '📱 Send Link to Borrower' }}
            </button>

            <button class="btn btn-sec"
              (click)="checkSignStatus()"
              [disabled]="isCheckingStatus"
              title="Refresh signature status from Digio Gateway">
              <span *ngIf="isCheckingStatus" class="spinner-border spinner-border-sm me-1"></span>
              {{ isCheckingStatus ? 'Checking...' : '🔄 Check Status' }}
            </button>
          </div>
        </div>

        <!-- ACTIVE SIGNING LINK PANEL (IF GENERATED) -->
        <div class="signing-link-box" *ngIf="signingUrl">
          <div class="sl-header">
            <div class="sl-title">
              <span class="pulse-dot"></span>
              <span>Active Signing Session</span>
            </div>
            <div style="display:flex; gap:6px;">
              <button class="btn-copy" (click)="openSigningUrl()">🚀 Open Signing Window</button>
              <button class="btn-copy" (click)="copySigningLink()">📋 Copy Link</button>
            </div>
          </div>
          <div class="sl-url-display">{{ signingUrl }}</div>
          <div class="sl-footer">
            <span>Awaiting Aadhaar OTP signature from {{ borrowerMobile }}</span>
            <span *ngIf="pollingSeconds > 0" class="polling-clock">⏱ Polling: {{ pollingSeconds }}s</span>
          </div>
        </div>

        <!-- DIGIO LIVE DOCUMENT REF -->
        <div class="digio-id-bar" *ngIf="digioDocumentId">
          <span class="lbl">Digio Doc ID:</span>
          <span class="val font-mono">{{ digioDocumentId }}</span>
        </div>
      </div>

    </div>

    <!-- RIGHT COLUMN: Workflow Tracker & Live Signature Seal -->
    <div class="right-col">

      <!-- CARD 4: 4-STAGE EXECUTION WORKFLOW TRACKER -->
      <div class="card">
        <div class="card-title">
          <div class="ct-bar"></div>
          <span>Execution Lifecycle Tracker</span>
        </div>

        <div class="flow-steps">
          <!-- Step 1: e-Stamp -->
          <div class="flow-step done">
            <div class="step-num">✓</div>
            <div class="step-content">
              <div class="step-title">e-Stamp Procured &amp; Merged</div>
              <div class="step-desc">
                NeSL Certificate #{{ eStampCertNo }} attached to agreement
              </div>
            </div>
            <span class="badge b-g">Done</span>
          </div>

          <!-- Step 2: Borrower Sign -->
          <div class="flow-step" [class.done]="borrowerSigned" [class.active]="!borrowerSigned">
            <div class="step-num">{{ borrowerSigned ? '✓' : '2' }}</div>
            <div class="step-content">
              <div class="step-title">Borrower Aadhaar OTP Signature</div>
              <div class="step-desc">
                {{ borrowerSigned ? ('Verified on ' + signedTimestamp) : 'Awaiting borrower Aadhaar authentication' }}
              </div>
            </div>
            <span class="badge" [class.b-g]="borrowerSigned" [class.b-amber]="!borrowerSigned">
              {{ borrowerSigned ? 'Signed' : 'Pending' }}
            </span>
          </div>

          <!-- Step 3: NBFC DSC Sign -->
          <div class="flow-step" [class.done]="nbfcSigned" [class.active]="borrowerSigned && !nbfcSigned">
            <div class="step-num">{{ nbfcSigned ? '✓' : '3' }}</div>
            <div class="step-content">
              <div class="step-title">NBFC Co-Signature (DSC)</div>
              <div class="step-desc">
                {{ nbfcSigned ? 'Digital Signature Certificate applied automatically' : 'Awaiting borrower completion' }}
              </div>
            </div>
            <span class="badge" [class.b-g]="nbfcSigned" [class.b-gray]="!nbfcSigned">
              {{ nbfcSigned ? 'Done' : 'Pending' }}
            </span>
          </div>

          <!-- Step 4: Vault & Storage -->
          <div class="flow-step" [class.done]="pdfStored" [class.active]="nbfcSigned && !pdfStored">
            <div class="step-num">{{ pdfStored ? '✓' : '4' }}</div>
            <div class="step-content">
              <div class="step-title">Immutable Archival &amp; Audit Trail</div>
              <div class="step-desc">
                {{ pdfStored ? 'Tamper-evident signed PDF stored in LOS repository' : 'Pending final sealing' }}
              </div>
            </div>
            <span class="badge" [class.b-g]="pdfStored" [class.b-gray]="!pdfStored">
              {{ pdfStored ? 'Secured' : 'Pending' }}
            </span>
          </div>
        </div>
      </div>

      <!-- CARD 5: DIGITAL SIGNATURE SEAL & AUDIT CERTIFICATE -->
      <div class="card cert-seal-card" [class.sealed]="pdfStored">
        <div class="card-title">
          <div class="ct-bar"></div>
          <span>Digital Signature Seal &amp; Audit Certificate</span>
        </div>

        <div class="seal-visual" *ngIf="pdfStored">
          <div class="seal-badge">
            <div class="seal-icon">🛡️</div>
            <div class="seal-text">
              <div class="seal-h1">DIGITALLY SIGNED &amp; VERIFIED</div>
              <div class="seal-h2">Information Technology Act, 2000</div>
            </div>
          </div>

          <div class="signatures-grid">
            <div class="sig-box">
              <div class="sig-type">BORROWER SIGNATURE</div>
              <div class="sig-name">{{ borrowerName }}</div>
              <div class="sig-meta">Aadhaar OTP Auth · UIDAI</div>
              <div class="sig-ts">{{ signedTimestamp }}</div>
            </div>
            <div class="sig-box nbfc">
              <div class="sig-type">NBFC AUTHORIZED SIGNATORY</div>
              <div class="sig-name">Nishwik Financial Services</div>
              <div class="sig-meta">Class 3 Digital Signature Certificate</div>
              <div class="sig-ts">{{ signedTimestamp }}</div>
            </div>
          </div>

          <div class="audit-hash-bar" *ngIf="docChecksum">
            <span class="lbl">Checksum:</span>
            <span class="val font-mono">{{ docChecksum }}</span>
          </div>

          <div class="download-action-row">
            <button class="btn btn-pri w-100 download-btn"
              (click)="downloadAgreement()"
              [disabled]="isDownloading">
              <span *ngIf="isDownloading" class="spinner-border spinner-border-sm me-2"></span>
              ⬇️ Download Signed Agreement PDF (with Audit Trail)
            </button>
          </div>
        </div>

        <!-- Pending State Placeholder -->
        <div class="seal-pending" *ngIf="!pdfStored">
          <div class="pending-icon">⏳</div>
          <div class="pending-title">Awaiting Signature Completion</div>
          <p class="pending-sub">
            Once the borrower signs via Aadhaar OTP, the counter-signed agreement with CCA audit certificate will appear here.
          </p>
        </div>
      </div>

    </div>

  </div>

  <!-- NAVIGATION BOTTOM BAR -->
  <div class="nav-bar esign-nav">
    <button class="btn btn-sec btn-lg" (click)="prev.emit()">
      ← Back to eStamp
    </button>
    <button class="btn btn-pri btn-lg next-btn"
      (click)="proceedToNext()"
      [disabled]="!pdfStored">
      Agreement Signed → Proceed to eMandate (Step 11) →
    </button>
  </div>

</div>
